<script>
    import { onMount, onDestroy } from "svelte";
    import { Chart, registerables, CategoryScale, LinearScale, BarController, BarElement, Tooltip, Legend } from "chart.js";
    import annotationPlugin from "chartjs-plugin-annotation";
    import { theme } from "$stores/themeStore";
    import { get } from "svelte/store";
    import { selectedStudy, selectedParticipant } from "$stores/selectionStore";
    import { studyProperties, fetchStudyProperties, isFeatureEnabled } from "$stores/configStore";

    Chart.register(
      ...registerables,
      CategoryScale,
      LinearScale,
      BarController,
      BarElement,
      Tooltip,
      Legend,
      annotationPlugin
    );

    export let data = [];
    export let linePercentage = 80; // Will be overridden by study properties
    export let thresholdBars = 7; // Will be overridden by study properties

    let canvas;
    let chart;
    let chartBorderColor = "rgba(160, 160, 160, 0.9)";
    let chartBackgroundColor = "rgba(160, 160, 160, 0.9)";

    const lightModeOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                title: {
                    display: false,
                    text: "Serial Number",
                    color: "black",
                },
                ticks: {
                    color: "black",
                    maxRotation: 90,
                    minRotation: 90,
                    callback: function(value, index) {
                        if (data && data[index] && data[index].day) {
                            // Parse the date string directly to avoid timezone issues
                            const [year, month, day] = data[index].day.split('-');
                            return `${parseInt(month)}/${parseInt(day)}`;
                        }
                        return value;
                    }
                },
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Count",
                    color: "black",
                },
                position: "right",
                ticks: {
                    color: "black",
                    stepSize: 1,
                    callback: function(value) {
                        return Number.isInteger(value) ? value : null;
                    }
                },
                max: data.length > 0 ? Math.max(...data.map(item => item.count)) + 1 : 10,
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            canvas: {
                responsive: true,
                maintainAspectRatio: false,
                resizeDelay: 1000,
            },
            annotation: {
                annotations: {
                    line1: {
                        type: "line",
                        yMin: linePercentage,
                        yMax: linePercentage,
                        borderColor: "rgba(120, 20, 80, 1.0)",
                        borderWidth: 0.5,
                        label: {
                            display: true,
                            content: `${linePercentage}%`,
                            position: "end",
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            color: "rgba(120, 20, 80, 1.0)",
                            font: {
                                size: 10
                            }
                        }
                    },
                },
            },
        },
    };

    const darkModeOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                title: {
                    display: false,
                    text: "Serial Number",
                    color: "white",
                },
                ticks: {
                    color: "white",
                    maxRotation: 90,
                    minRotation: 90,
                    callback: function(value, index) {
                        if (data && data[index] && data[index].day) {
                            // Parse the date string directly to avoid timezone issues
                            const [year, month, day] = data[index].day.split('-');
                            return `${parseInt(month)}/${parseInt(day)}`;
                        }
                        return value;
                    }
                },
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Count",
                    color: "white",
                },
                position: "right",
                ticks: {
                    color: "white",
                    stepSize: 1,
                    callback: function(value) {
                        return Number.isInteger(value) ? value : null;
                    }
                },
                max: data.length > 0 ? Math.max(...data.map(item => item.count)) + 1 : 10,
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            canvas: {
                responsive: true,
                maintainAspectRatio: false,
                resizeDelay: 1000,
            },
            annotation: {
                annotations: {
                    line1: {
                        type: "line",
                        yMin: linePercentage,
                        yMax: linePercentage,
                        borderColor: "rgba(110, 210, 170, 0.8)",
                        borderWidth: 0.5,
                        label: {
                            display: true,
                            content: `${linePercentage}%`,
                            position: "end",
                            backgroundColor: "rgba(50, 50, 50, 0.8)",
                            color: "rgba(110, 210, 170, 0.8)",
                            font: {
                                size: 10
                            }
                        }
                    },
                },
            },
        },
    };

    function getBarColors() {
        let backgrounds = [];
        let borders = [];
        
        // Check if randomization feature is enabled and participant has randomization date
        const isRandomizationEnabled = isFeatureEnabled('randomization.enabled');
        const randomizationDate = $selectedParticipant?.randomizationDate;
        
        // Color pairs for each theme
        const lightColors = {
            below: { bg: "rgba(140, 140, 140, 0.4)", border: "rgba(140, 140, 140, 0.75)" },
            above: { bg: "rgba(34, 197, 94, 0.4)", border: "rgba(34, 197, 94, 0.75)" },
            randomizationBelow: { bg: "rgba(140, 140, 140, 0.4)", border: "rgba(0, 0, 0, 0.75)" }, // Black border for randomization below threshold
            randomizationAbove: { bg: "rgba(34, 197, 94, 0.4)", border: "rgba(0, 0, 0, 0.75)" } // Black border for randomization above threshold
        };
        
        const darkColors = {
            below: { bg: "rgba(120, 120, 120, 0.4)", border: "rgba(120, 120, 120, 0.75)" },
            above: { bg: "rgba(34, 197, 94, 0.4)", border: "rgba(34, 197, 94, 0.75)" },
            randomizationBelow: { bg: "rgba(120, 120, 120, 0.4)", border: "rgba(255, 255, 255, 0.75)" }, // White border for randomization below threshold
            randomizationAbove: { bg: "rgba(34, 197, 94, 0.4)", border: "rgba(255, 255, 255, 0.75)" } // White border for randomization above threshold
        };

        const colors = get(theme) === "dark" ? darkColors : lightColors;
        
        // Assign colors based on value compared to threshold, but only for first thresholdBars
        data.forEach((item, index) => {
            const isAboveThreshold = index < thresholdBars ? item.count >= thresholdValue : false;
            
            // Check if this is the randomization date
            let isRandomizationDay = false;
            if (isRandomizationEnabled && randomizationDate && item.day) {
                // Parse item date directly
                const [itemYear, itemMonth, itemDay] = item.day.split('-');
                
                // Parse randomization date - handle both ISO string and date string formats
                let randYear, randMonth, randDay;
                if (typeof randomizationDate === 'string') {
                    if (randomizationDate.includes('T')) {
                        // ISO string format: "2025-07-21T00:00:00.000Z"
                        [randYear, randMonth, randDay] = randomizationDate.split('T')[0].split('-');
                    } else {
                        // Date string format: "2025-07-21"
                        [randYear, randMonth, randDay] = randomizationDate.split('-');
                    }
                } else if (randomizationDate instanceof Date) {
                    // Date object
                    randYear = randomizationDate.getFullYear().toString();
                    randMonth = (randomizationDate.getMonth() + 1).toString().padStart(2, '0');
                    randDay = randomizationDate.getDate().toString().padStart(2, '0');
                }
                
                // Compare year, month, and day directly
                isRandomizationDay = itemYear === randYear && itemMonth === randMonth && itemDay === randDay;
                
            }
            
            if (isRandomizationDay) {
                if (isAboveThreshold) {
                    backgrounds.push(colors.randomizationAbove.bg);
                    borders.push(colors.randomizationAbove.border);
                } else {
                    backgrounds.push(colors.randomizationBelow.bg);
                    borders.push(colors.randomizationBelow.border);
                }
            } else {
                backgrounds.push(isAboveThreshold ? colors.above.bg : colors.below.bg);
                borders.push(isAboveThreshold ? colors.above.border : colors.below.border);
            }
        });
        
        return { backgrounds, borders };
    }

    function destroyChart() {
        if (chart) {
            chart.destroy();
            chart = null;
        }
        if (canvas) {
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }

    function createChart() {
        if (!canvas) {
            console.warn('Canvas element not found');
            return;
        }

        destroyChart();

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            console.error('Unable to get 2D context from canvas');
            return;
        }

        const currentTheme = get(theme);
        const options = currentTheme === "dark" ? darkModeOptions : lightModeOptions;
        // Update annotation config with dynamic thresholdValue and label
        options.plugins.annotation = {
            annotations: {
                line1: {
                    type: "line",
                    yMin: thresholdValue,
                    yMax: thresholdValue,
                    borderColor: currentTheme === "dark" ? "rgba(110, 210, 170, 0.8)" : "rgba(120, 20, 80, 1.0)",
                    borderWidth: 0.5,
                    label: {
                        display: true,
                        content: `${linePercentage}%`,
                        position: "end",
                        backgroundColor: currentTheme === "dark" ? "rgba(50,50,50,0.8)" : "rgba(255,255,255,0.8)",
                        color: currentTheme === "dark" ? "rgba(110, 210, 170, 0.8)" : "rgba(120, 20, 80, 1.0)",
                        font: { size: 10 }
                    }
                }
            }
        };

        const { backgrounds, borders } = getBarColors();

        try {
            chart = new Chart(ctx, {
                type: "bar",
                data: {
                    labels: data.map((item) => item.day),
                    datasets: [{
                        label: "Count",
                        data: data.map((item) => item.count),
                        backgroundColor: backgrounds,
                        borderColor: borders,
                        borderWidth: 1,
                    }],
                },
                options: options,
            });
        } catch (error) {
            console.error('Error creating chart:', error);
        }
    }

    function handleThemeChange() {
        chartBorderColor = get(theme) === "dark" ? "rgba(120, 120, 120, 0.75)" : "rgba(140, 140, 140, 0.75)";
        chartBackgroundColor = get(theme) === "dark" ? "rgba(120, 120, 120, 0.4)" : "rgba(140, 140, 140, 0.4)";
        if (canvas) {
            createChart();
        }
    }

    onMount(async () => {
        // Fetch study properties to get SMBG compliance settings
        if ($selectedStudy?._id) {
            await fetchStudyProperties($selectedStudy._id);
        }
    });

    // Update linePercentage and thresholdBars from study properties
    $: if ($studyProperties?.glucoseParameters?.smbgCompliance) {
        linePercentage = $studyProperties.glucoseParameters.smbgCompliance.complianceThreshold;
        thresholdBars = $studyProperties.glucoseParameters.smbgCompliance.complianceFocus;
    }
    $: targetReadingCount = $studyProperties?.glucoseParameters?.smbgCompliance?.targetReadingCount || 4;
    $: thresholdValue = targetReadingCount * (linePercentage / 100);

    onDestroy(() => {
        destroyChart();
    });

    $: if (data && data.length > 0 && canvas) {
        setTimeout(() => {
            createChart();
        }, 0);
    }

    $: if ($theme && canvas) {
        handleThemeChange();
    }
</script>

<div class="w-full h-64 flex justify-center">
    {#if !data || data.length === 0}
        <div class="w-full h-full flex items-center justify-center">
            <div class="text-gray-500">No SMBG data available</div>
        </div>
    {:else}
        <div class="w-full h-full">
            <canvas bind:this={canvas}></canvas>
        </div>
    {/if}
</div>